/* eslint-disable max-lines-per-function */
import { AuxButton, AuxInlineMenu, AuxPopover, AuxSelect } from "@blk/aladdin-react-components-es";
import { AuxButtonTypeEnum, AuxDynamicPositionEnum } from "@blk/aladdin-web-components";
import "./insWorkspaceActionBar.scss";
import React, { useCallback, useMemo } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useHandleSetDefaultWorkspace } from "./hooks/handleSetDefaultWorkspace";
import { useGetCurrentWorkspace } from "./hooks/handleWorkspacePreferences";
import { globalWorkspaceSelector, useHandleSelectWorksapce } from "./insWorkspaceLeftPane";
import { CurrentWorkspaceModalType, WorkspaceEntityType } from "./store/types";
import { setCurrentWorkspaceModalType, setCurrentWorkspaceName, setCurrentWorkspacePrivacy } from "./store/workspaces";
import { MessagesDialogFunctionName } from "components/frame/helper";
import { ModalName } from "components/modal/types";
import { Permissions, usePermission } from "feature-flag";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setMessagesDialog } from "store/messages-dialog";
import { openModal } from "store/modal";
import { getCurrentLocalTime } from "common/date-utils";
const areWorkspacesDisabled = (hasLightPortfolioAnalytics, entityType) => {
    // Fund series (IDD) has workspaces disabled
    if (entityType === WorkspaceEntityType.FUND_SERIES) {
        return true;
    }
    // Light portfolio analytics for preinvestment always have workspaces disabled
    if (hasLightPortfolioAnalytics) {
        return true;
    }
    return false;
};
export const InsWorkspaceActionBar = (props) => {
    const dispatch = useDispatch();
    const accessSharedFolderPermission = usePermission([Permissions.ACCESS_SHARED_FOLDER]);
    const { handleSetDefaultWorkspace } = useHandleSetDefaultWorkspace();
    const { currentWorkspace: workspace, workspacesAndPreferences: options } = useSelector((state) => globalWorkspaceSelector(state));
    const currentWorkspace = useGetCurrentWorkspace(workspace, options);
    const { handleSelect } = useHandleSelectWorksapce();
    const hasLightPortfolioAnalytics = usePermission([Permissions.LIGHT_PORTFOLIO_ANALYTICS]);
    const unhiddenWorkspaces = useMemo(() => options?.filter((w) => w.isHidden === false), [options]);
    const workspacesDisabled = areWorkspacesDisabled(hasLightPortfolioAnalytics, currentWorkspace?.entityType);
    const actionMenuItems = useMemo(() => [
        [
            {
                eventData: {
                    action: () => {
                        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.DefaultWorkspaceClicked,
                            properties: { LocalTimestamp: getCurrentLocalTime() } }, {
                            defaultWorkspaceName: currentWorkspace?.name
                        });
                        if (currentWorkspace) {
                            handleSetDefaultWorkspace(currentWorkspace);
                        }
                    }
                },
                hasEmptyIcon: true,
                isDisabled: currentWorkspace?.isDefault,
                label: language.en.workspace.setDefaultLable,
                leftIcon: "checkmark"
            }
        ],
        [
            {
                eventData: { action: () => batch(() => {
                        dispatch(setCurrentWorkspacePrivacy(currentWorkspace?.isPrivate));
                        dispatch(setCurrentWorkspaceName(currentWorkspace?.name));
                        dispatch(setCurrentWorkspaceModalType(CurrentWorkspaceModalType.Edit));
                        dispatch(openModal({ content: { id: "editWorkspace", modalName: ModalName.EditDuplicateWorkspace },
                            title: "Edit Workspace",
                            type: "action" }));
                    }) },
                isDisabled: !currentWorkspace?.isEditable || (!currentWorkspace.isPrivate && !accessSharedFolderPermission),
                label: language.en.workspace.settingsLabel,
                leftIcon: "edit"
            },
            {
                eventData: { action: () => batch(() => {
                        dispatch(setCurrentWorkspacePrivacy(currentWorkspace?.isPrivate));
                        dispatch(setCurrentWorkspaceName(currentWorkspace?.name));
                        dispatch(setCurrentWorkspaceModalType(CurrentWorkspaceModalType.Duplicate));
                        dispatch(openModal({ content: { id: "duplicateWorkspace", modalName: ModalName.EditDuplicateWorkspace },
                            title: "Duplicate Workspace",
                            type: "action" }));
                    }) },
                label: language.en.workspace.duplicateLabel,
                leftIcon: "duplicate"
            },
            {
                eventData: { action: () => {
                        appInsightsTrackEvent({
                            name: appInsightEvents.customEvents.names.DeleteWorkspace,
                            properties: { LocalTimestamp: getCurrentLocalTime() }
                        }, {
                            deleteWorkspaceName: currentWorkspace?.name
                        });
                        dispatch(setMessagesDialog({ functionName: MessagesDialogFunctionName.DeleteWorkspace,
                            hasCustomFooter: true,
                            isPrimaryButtonDelete: true,
                            messages: [{ message: language.en.workspace.deleteMessage(currentWorkspace?.name) }],
                            title: language.en.workspace.deleteHeader,
                            type: "alert-with-options" }));
                    } },
                hasAlert: true,
                iconState: currentWorkspace?.isEditable ? "alert" : "tertiary-subtle-button",
                isDisabled: !currentWorkspace?.isEditable || (!currentWorkspace.isPrivate && !accessSharedFolderPermission),
                label: language.en.workspace.deleteLabel,
                leftIcon: "delete-small"
            }
        ],
        [
            {
                eventData: { action: () => dispatch(openModal({
                        content: { id: "manageWorkspaces", modalName: ModalName.ManageWorkspaces, parameters: { queryState: props.workspacePreferencesQueryState } },
                        title: language.en.workspace.manageWorkspaceLabel,
                        type: "action"
                    })) },
                label: language.en.workspace.manageWorkspaceLabel,
                leftIcon: "settings"
            },
            {
                eventData: { action: () => batch(() => {
                        dispatch(setCurrentWorkspacePrivacy(!accessSharedFolderPermission));
                        dispatch(openModal({ content: { id: "createNewWorkspace", modalName: ModalName.CreateNewWorkspace },
                            title: language.en.workspace.createWorkspaceLabel,
                            type: "action" }));
                    }) },
                label: language.en.workspace.createWorkspaceLabel,
                leftIcon: "add"
            }
        ]
    ], [accessSharedFolderPermission, currentWorkspace, dispatch, handleSetDefaultWorkspace, props.workspacePreferencesQueryState]);
    const selectOptions = useMemo(() => [
        {
            values: unhiddenWorkspaces
                ?.map((option) => ({
                displayValue: option.name,
                isSelected: currentWorkspace?.id === option.id,
                value: option.id
            })) || []
        }
    ], [unhiddenWorkspaces, currentWorkspace?.id]);
    const handleSelectionChanged = useCallback((e) => {
        const selectedOption = e.detail.value;
        const workspaceSelection = unhiddenWorkspaces?.find((option) => option.id === selectedOption.value);
        if (currentWorkspace?.id !== selectedOption.value) {
            handleSelect(workspaceSelection);
        }
    }, [currentWorkspace?.id, handleSelect, unhiddenWorkspaces]);
    return (React.createElement("div", { className: "rootWorkspaceContainer" },
        React.createElement("div", { className: "workspace" },
            React.createElement("div", { className: "workspaceContainer" },
                React.createElement("div", { className: "workspaceHeader" },
                    React.createElement(AuxSelect, { data: selectOptions, isDisabled: workspacesDisabled, size: "regular", type: "simple", shouldCloseOverlays: true, highlightMode: "dynamic", selected: currentWorkspace?.id, isCompact: false, hasInitialOptionPlaceholder: false, onSelectionChanged: handleSelectionChanged }),
                    workspacesDisabled
                        ? React.createElement(AuxPopover, { overridePlacement: AuxDynamicPositionEnum.BOTTOM_CENTER, trigger: "hover" },
                            React.createElement(AuxButton, { slot: "target", type: AuxButtonTypeEnum.SECONDARY, icon: "action" }),
                            React.createElement("div", { slot: "content" }, language.en.workspace.workspaceDisabledMessage))
                        : React.createElement(AuxInlineMenu, { overridePlacement: AuxDynamicPositionEnum.BOTTOM_RIGHT, data: actionMenuItems, onInlineMenuItemClicked: (e) => e.detail.element.eventData?.action(), slot: "target", isActive: false },
                            React.createElement(AuxButton, { slot: "target", type: AuxButtonTypeEnum.SECONDARY, icon: "action" })))))));
};
